import React, { useState } from 'react';
import { Card, Row, Col, Tab, Nav, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { contactService } from '../../shared/services/apiService';
import './Contact.css';

const Contact = () => {
  const [activeTab, setActiveTab] = useState('contact');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);
    setValidationErrors({});

    try {
      const response = await contactService.sendMessage(formData);
      
      if (response.data.success) {
        setSuccess(true);
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
        
        // Auto-hide success message after 5 seconds
        setTimeout(() => setSuccess(false), 5000);
      }
    } catch (err) {
      if (err.response?.status === 422) {
        // Validation errors
        setValidationErrors(err.response.data.errors || {});
        setError('Veuillez corriger les erreurs dans le formulaire.');
      } else {
        setError(
          err.response?.data?.message || 
          'Une erreur est survenue lors de l\'envoi du message. Veuillez réessayer.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-contact-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-content">
          <div>
            <h2 className="page-title">
              <i className="fa fa-envelope me-3"></i>
              Contactez-nous
            </h2>
            <p className="page-subtitle">
              Nous sommes là pour vous aider et répondre à toutes vos questions
            </p>
          </div>
        </div>
      </div>

      {/* Contact Info Cards */}
      <Row className="mb-4">
        <Col xl={3} lg={6} sm={6}>
          <Card className="contact-info-card card-primary">
            <Card.Body>
              <div className="contact-icon">
                <i className="fa fa-phone-alt"></i>
              </div>
              <div className="contact-content">
                <h4 className="contact-title">Téléphone</h4>
                <p className="contact-detail">+221 77 499 44 88</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={3} lg={6} sm={6}>
          <Card className="contact-info-card card-secondary">
            <Card.Body>
              <div className="contact-icon">
                <i className="fa fa-envelope"></i>
              </div>
              <div className="contact-content">
                <h4 className="contact-title">Email</h4>
                <p className="contact-detail">contact@senoffre.com</p>
                <p className="contact-detail">support@senoffre.com</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={3} lg={6} sm={6}>
          <Card className="contact-info-card card-success">
            <Card.Body>
              <div className="contact-icon">
                <i className="fa fa-map-marker-alt"></i>
              </div>
              <div className="contact-content">
                <h4 className="contact-title">Adresse</h4>
                <p className="contact-detail">Dakar, Sénégal</p>
                <p className="contact-detail">Cité Keur Gorgui, Villa N°26, Dakar</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={3} lg={6} sm={6}>
          <Card className="contact-info-card card-warning">
            <Card.Body>
              <div className="contact-icon">
                <i className="fa fa-clock"></i>
              </div>
              <div className="contact-content">
                <h4 className="contact-title">Horaires</h4>
                <p className="contact-detail">Lun - Ven: 8h - 18h</p>
                <p className="contact-detail">Sam: 9h - 13h</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Main Content with Tabs */}
      <Card className="contact-content-card">
        <Card.Body>
          <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
            <Nav variant="pills" className="contact-tabs mb-4">
              <Nav.Item>
                <Nav.Link eventKey="contact">
                  <i className="fa fa-paper-plane me-2"></i>
                  Formulaire de Contact
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="support">
                  <i className="fa fa-headset me-2"></i>
                  Support Technique
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="social">
                  <i className="fa fa-share-alt me-2"></i>
                  Réseaux Sociaux
                </Nav.Link>
              </Nav.Item>
            </Nav>

            <Tab.Content>
              {/* Contact Form Tab */}
              <Tab.Pane eventKey="contact">
                <div className="tab-content-wrapper">
                  <div className="content-header">
                    <h3 className="content-title">Envoyez-nous un message</h3>
                    <p className="content-subtitle">
                      Remplissez le formulaire ci-dessous et nous vous répondrons dans les plus brefs délais
                    </p>
                  </div>
                  
                  <Row>
                    <Col lg={8} className="mx-auto">
                      {success && (
                        <Alert variant="success" dismissible onClose={() => setSuccess(false)}>
                          <i className="fa fa-check-circle me-2"></i>
                          Votre message a été envoyé avec succès. Nous vous répondrons dans les plus brefs délais.
                        </Alert>
                      )}
                      
                      {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)}>
                          <i className="fa fa-exclamation-circle me-2"></i>
                          {error}
                        </Alert>
                      )}
                      
                      <Form onSubmit={handleSubmit} className="contact-form">
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-4">
                              <Form.Label>
                                <i className="fa fa-user me-2"></i>
                                Nom complet <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="Votre nom complet"
                                isInvalid={!!validationErrors.name}
                                required
                                minLength={2}
                                maxLength={100}
                              />
                              {validationErrors.name && (
                                <Form.Control.Feedback type="invalid">
                                  {validationErrors.name[0]}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-4">
                              <Form.Label>
                                <i className="fa fa-envelope me-2"></i>
                                Adresse email <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                placeholder="votre@email.com"
                                isInvalid={!!validationErrors.email}
                                required
                              />
                              {validationErrors.email && (
                                <Form.Control.Feedback type="invalid">
                                  {validationErrors.email[0]}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>
                        
                        <Form.Group className="mb-4">
                          <Form.Label>
                            <i className="fa fa-phone me-2"></i>
                            Téléphone
                          </Form.Label>
                          <Form.Control
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="+221 77 123 45 67"
                            isInvalid={!!validationErrors.phone}
                          />
                          {validationErrors.phone && (
                            <Form.Control.Feedback type="invalid">
                              {validationErrors.phone[0]}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                        
                        <Form.Group className="mb-4">
                          <Form.Label>
                            <i className="fa fa-tag me-2"></i>
                            Sujet <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="subject"
                            value={formData.subject}
                            onChange={handleInputChange}
                            placeholder="Sujet de votre message"
                            isInvalid={!!validationErrors.subject}
                            required
                            minLength={5}
                            maxLength={200}
                          />
                          {validationErrors.subject && (
                            <Form.Control.Feedback type="invalid">
                              {validationErrors.subject[0]}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                        
                        <Form.Group className="mb-4">
                          <Form.Label>
                            <i className="fa fa-comment-alt me-2"></i>
                            Message <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={6}
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            placeholder="Écrivez votre message ici..."
                            isInvalid={!!validationErrors.message}
                            required
                            minLength={10}
                            maxLength={2000}
                          />
                          {validationErrors.message && (
                            <Form.Control.Feedback type="invalid">
                              {validationErrors.message[0]}
                            </Form.Control.Feedback>
                          )}
                          <Form.Text className="text-muted">
                            {formData.message.length}/2000 caractères
                          </Form.Text>
                        </Form.Group>
                        
                        <div className="text-center">
                          <Button 
                            type="submit" 
                            className="btn-send-message"
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <Spinner
                                  as="span"
                                  animation="border"
                                  size="sm"
                                  role="status"
                                  aria-hidden="true"
                                  className="me-2"
                                />
                                Envoi en cours...
                              </>
                            ) : (
                              <>
                                <i className="fa fa-paper-plane me-2"></i>
                                Envoyer le message
                              </>
                            )}
                          </Button>
                        </div>
                      </Form>
                    </Col>
                  </Row>
                </div>
              </Tab.Pane>

              {/* Support Tab */}
              <Tab.Pane eventKey="support">
                <div className="tab-content-wrapper">
                  <div className="content-header">
                    <h3 className="content-title">Support Technique</h3>
                    <p className="content-subtitle">
                      Besoin d'aide ? Notre équipe est là pour vous
                    </p>
                  </div>
                  
                  <Row>
                    <Col lg={4} className="mb-4">
                      <div className="support-card">
                        <div className="support-icon">
                          <i className="fa fa-life-ring"></i>
                        </div>
                        <h4>Assistance en ligne</h4>
                        <p>
                          Chattez en direct avec notre équipe de support pour une aide immédiate.
                        </p>
                        <Button variant="outline-primary" className="mt-3">
                          <i className="fa fa-comments me-2"></i>
                          Démarrer le chat
                        </Button>
                      </div>
                    </Col>
                    <Col lg={4} className="mb-4">
                      <div className="support-card">
                        <div className="support-icon">
                          <i className="fa fa-book"></i>
                        </div>
                        <h4>Documentation</h4>
                        <p>
                          Consultez notre documentation complète pour trouver des réponses rapidement.
                        </p>
                        <Button variant="outline-primary" className="mt-3">
                          <i className="fa fa-file-alt me-2"></i>
                          Voir la documentation
                        </Button>
                      </div>
                    </Col>
                    <Col lg={4} className="mb-4">
                      <div className="support-card">
                        <div className="support-icon">
                          <i className="fa fa-video"></i>
                        </div>
                        <h4>Tutoriels vidéo</h4>
                        <p>
                          Apprenez à utiliser SenOffre grâce à nos tutoriels vidéo détaillés.
                        </p>
                        <Button variant="outline-primary" className="mt-3">
                          <i className="fa fa-play me-2"></i>
                          Voir les tutoriels
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  <div className="support-hours mt-5">
                    <h4 className="text-center mb-4">Horaires du Support</h4>
                    <Row>
                      <Col md={6} className="mb-3">
                        <div className="hours-item">
                          <i className="fa fa-calendar-day me-3"></i>
                          <div>
                            <strong>Lundi - Vendredi</strong>
                            <p className="mb-0">8h00 - 18h00 (GMT)</p>
                          </div>
                        </div>
                      </Col>
                      <Col md={6} className="mb-3">
                        <div className="hours-item">
                          <i className="fa fa-calendar-week me-3"></i>
                          <div>
                            <strong>Samedi</strong>
                            <p className="mb-0">9h00 - 13h00 (GMT)</p>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </div>
              </Tab.Pane>

              {/* Social Media Tab */}
              <Tab.Pane eventKey="social">
                <div className="tab-content-wrapper">
                  <div className="content-header">
                    <h3 className="content-title">Suivez-nous sur les Réseaux Sociaux</h3>
                    <p className="content-subtitle">
                      Restez connecté avec SenOffre et ne manquez aucune actualité
                    </p>
                  </div>
                  
                  <Row className="justify-content-center">
                    <Col lg={3} md={6} className="mb-4">
                      <div className="social-card facebook">
                        <div className="social-icon">
                          <i className="fab fa-facebook-f"></i>
                        </div>
                        <h4>Facebook</h4>
                        <p className="followers">12.5K abonnés</p>
                        <Button className="social-btn">
                          <i className="fab fa-facebook-f me-2"></i>
                          Suivre
                        </Button>
                      </div>
                    </Col>
                    <Col lg={3} md={6} className="mb-4">
                      <div className="social-card twitter">
                        <div className="social-icon">
                          <i className="fab fa-twitter"></i>
                        </div>
                        <h4>Twitter</h4>
                        <p className="followers">8.3K abonnés</p>
                        <Button className="social-btn">
                          <i className="fab fa-twitter me-2"></i>
                          Suivre
                        </Button>
                      </div>
                    </Col>
                    <Col lg={3} md={6} className="mb-4">
                      <div className="social-card linkedin">
                        <div className="social-icon">
                          <i className="fab fa-linkedin-in"></i>
                        </div>
                        <h4>LinkedIn</h4>
                        <p className="followers">15.2K abonnés</p>
                        <Button className="social-btn">
                          <i className="fab fa-linkedin-in me-2"></i>
                          Suivre
                        </Button>
                      </div>
                    </Col>
                    <Col lg={3} md={6} className="mb-4">
                      <div className="social-card instagram">
                        <div className="social-icon">
                          <i className="fab fa-instagram"></i>
                        </div>
                        <h4>Instagram</h4>
                        <p className="followers">9.7K abonnés</p>
                        <Button className="social-btn">
                          <i className="fab fa-instagram me-2"></i>
                          Suivre
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  <div className="newsletter-section mt-5">
                    <div className="newsletter-card">
                      <div className="newsletter-icon">
                        <i className="fa fa-bell"></i>
                      </div>
                      <h4>Abonnez-vous à notre Newsletter</h4>
                      <p>Recevez les derniers Appels d'offres et actualités directement dans votre boîte mail</p>
                      <Form className="newsletter-form">
                        <div className="input-group">
                          <Form.Control
                            type="email"
                            placeholder="Votre adresse email"
                            className="newsletter-input"
                          />
                          <Button type="submit" className="newsletter-btn">
                            <i className="fa fa-paper-plane me-2"></i>
                            S'abonner
                          </Button>
                        </div>
                      </Form>
                    </div>
                  </div>
                </div>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Card.Body>
      </Card>
    </div>
  );
};

export default Contact;
